import React, { useEffect, useState } from 'react';
import '../../styles/animations.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faRulerCombined,
  faMoneyBillWave,
  faCalendarAlt,
  faMapMarkerAlt,
  faFileDownload,
  faPlus,
  faEdit,
  faTrash,
  faUser,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { sendApiRequest } from '../../utils.js';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

const Properties = () => {
  const userData = useSelector((state) => state.user_info[0]?.user);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAllPropertiesofCurrentUser = async () => {
    const response = await sendApiRequest('api/packages/all_manual/receipts', {}, 'GET');
    if (response.data.success) {
      // Filter receipts and transform to match UI structure
      const filteredReceipts = response.data.data.receipts
        .filter(receipt => receipt.customer.customerEmail === userData?.customerEmail && receipt.isCommissionReceipt === false)
        .map(receipt => ({
          _id: receipt._id,
          title: receipt.package.title, // Map package.title to title
          area: `${receipt.package.width * receipt.package.length}`, // Calculate area
          areaUnit: 'sq.ft', // Default unit
          value: receipt.amount, // Map amount to value
          location: receipt.remarks || 'Not specified', // Use remarks as location or default
          startDate: receipt.receiptDate, // Map receiptDate to startDate
          status: receipt.generatedManually ? 'Active' : 'Pending', // Derive status
          receiptNumber: receipt.receiptNumber,
          customerName: receipt.customer.customerName,
          customerEmail: receipt.customer.customerEmail,
          customerPhone: receipt.customer.customerPhone,
          partnerName: receipt.partner.fullName,
          partnerEmail: receipt.partner.email,
          partnerPhone: receipt.partner.phone,
          paymentMode: receipt.paymentMode,
          paymentDetails: receipt.paymentDetails,
          amountInWords: receipt.amountInWords,
          createdAt: receipt.createdAt,
          updatedAt: receipt.updatedAt
        }));


      setProperties(filteredReceipts);
      Swal.fire({
        icon: 'success',
        title: 'Properties loaded successfully',
        showConfirmButton: false,
        timer: 1500
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to load properties',
        showConfirmButton: false,
        timer: 1500
      });
    }
  };

  useEffect(() => {
    getAllPropertiesofCurrentUser();
  }, []);

  // const handleDeleteProperty = async (id) => {
  //   const response = await sendApiRequest('api/customers/properties', { id }, 'DELETE');
  //   if (response.status === 200) {
  //     getAllPropertiesofCurrentUser();
  //     Swal.fire({
  //       icon: 'success',
  //       title: 'Property deleted successfully',
  //       showConfirmButton: false,
  //       timer: 1500
  //     });
  //   } else {
  //     Swal.fire({
  //       icon: 'error',
  //       title: 'Failed to delete property',
  //       showConfirmButton: false,
  //       timer: 1500
  //     });
  //   }
  // };

  return (
    <div className="min-h-[30rem] bg-[#F1EFEC] p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 md:mb-8">
          <div className="flex items-center space-x-2 sm:space-x-4 mb-3 sm:mb-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#222222]">
              My Properties
            </h1>
            <div className="h-6 sm:h-8 w-px bg-[#1DCD9F] opacity-50"></div>
            <p className="text-sm sm:text-base text-[#222222]/80">{properties.length} Properties</p>
          </div>
        </div>

        <div>
          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
            {properties.map((property) => (
              <div
                key={property._id}
                className="bg-white rounded-xl border border-[#222222]/10 hover:shadow-lg hover:border-[#1DCD9F]/30 group transition-all duration-300"
              >
                {/* Property Card Header */}
                <div className="px-4 sm:px-6 py-3 sm:py-5 border-b border-[#222222]/10">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-[#222222] flex items-center group-hover:text-[#1DCD9F] transition-colors">
                        <div className="h-8 sm:h-10 w-8 sm:w-10 rounded-lg bg-[#1DCD9F]/10 flex items-center justify-center mr-2 sm:mr-3 group-hover:scale-110 transition-transform">
                          <FontAwesomeIcon icon={faHome} className="text-[#1DCD9F]" />
                        </div>
                        {property.title} (#{property.receiptNumber})
                      </h3>
                      <p className="text-xs sm:text-sm text-[#222222]/70 flex items-center mt-1 sm:mt-2 ml-10 sm:ml-13">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-[#1DCD9F]" />
                        {property.location}
                      </p>
                    </div>
                    <span className={`mt-2 sm:mt-0 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium ${
                      property.status === 'Active' ? 'bg-[#1DCD9F]/10 text-[#1DCD9F]' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {property.status}
                    </span>
                  </div>
                </div>

                {/* Property Details */}
                <div className="px-4 sm:px-6 py-3 sm:py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faRulerCombined} className="text-[#1DCD9F] mr-2 sm:mr-3" />
                      <div>
                        <p className="text-xs sm:text-sm text-[#222222]/70">Area</p>
                        <p className="text-xs sm:text-sm font-medium text-[#222222]">{property.area} {property.areaUnit}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="text-[#1DCD9F] mr-2 sm:mr-3" />
                      <div>
                        <p className="text-xs sm:text-sm text-[#222222]/70">Value</p>
                        <p className="text-xs sm:text-sm font-medium text-[#222222]">{formatCurrency(property.value)}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-[#1DCD9F] mr-2 sm:mr-3" />
                      <div>
                        <p className="text-xs sm:text-sm text-[#222222]/70">Receipt Date</p>
                        <p className="text-xs sm:text-sm font-medium text-[#222222]">{formatDate(property.startDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUser} className="text-[#1DCD9F] mr-2 sm:mr-3" />
                      <div>
                        <p className="text-xs sm:text-sm text-[#222222]/70">Customer</p>
                        <p className="text-xs sm:text-sm font-medium text-[#222222]">{property.customerName}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-[#F1EFEC] rounded-b-lg border-t border-[#222222]/10">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                    <button
                      onClick={() => setSelectedProperty(property)}
                      className="text-[#1DCD9F] hover:text-[#18b58c] font-medium text-xs sm:text-sm"
                    >
                      View Details
                    </button>
                    <div className="flex space-x-2">
                      {/* <button
                        onClick={() => handleDeleteProperty(property._id)}
                        className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-red-100 text-red-800 rounded-lg hover:bg-[#1DCD9F]/20 transition-colors text-xs sm:text-sm font-medium"
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />
                        Delete
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Property Details Modal */}
          {selectedProperty && (
            <div className="fixed inset-0 bg-[#222222]/80 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-3 sm:p-4">
              <div className="relative p-4 sm:p-6 md:p-8 w-full max-w-4xl shadow-lg rounded-lg bg-[#F1EFEC] border border-[#1DCD9F]/30">
                <div className="flex justify-between items-start">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#222222]">
                    Property Details - {selectedProperty.title} (#{selectedProperty.receiptNumber})
                  </h2>
                  <button
                    onClick={() => setSelectedProperty(null)}
                    className="text-[#222222]/50 hover:text-[#1DCD9F] transition-colors"
                  >
                    ×
                  </button>
                </div>
                <div className="mt-4 sm:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-white p-3 sm:p-5 rounded-lg border border-[#222222]/10">
                    <h3 className="font-medium text-base sm:text-lg text-[#222222] mb-2 sm:mb-3 pb-2 border-b border-[#222222]/10">Property Information</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Title:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.title}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Receipt Number:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.receiptNumber}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Area:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.area} {selectedProperty.areaUnit}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Location:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.location}</span></p>
                    </div>
                  </div>
                  <div className="bg-white p-3 sm:p-5 rounded-lg border border-[#222222]/10">
                    <h3 className="font-medium text-base sm:text-lg text-[#222222] mb-2 sm:mb-3 pb-2 border-b border-[#222222]/10">Financial Information</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Value:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{formatCurrency(selectedProperty.value)}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Amount in Words:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.amountInWords}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Payment Mode:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.paymentMode}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Payment Details:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.paymentDetails}</span></p>
                    </div>
                  </div>
                  <div className="bg-white p-3 sm:p-5 rounded-lg border border-[#222222]/10">
                    <h3 className="font-medium text-base sm:text-lg text-[#222222] mb-2 sm:mb-3 pb-2 border-b border-[#222222]/10">Customer Information</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Name:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.customerName}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Email:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.customerEmail}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Phone:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.customerPhone}</span></p>
                    </div>
                  </div>
                  <div className="bg-white p-3 sm:p-5 rounded-lg border border-[#222222]/10">
                    <h3 className="font-medium text-base sm:text-lg text-[#222222] mb-2 sm:mb-3 pb-2 border-b border-[#222222]/10">Partner Information</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Name:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.partnerName}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Email:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.partnerEmail}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Phone:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{selectedProperty.partnerPhone}</span></p>
                    </div>
                  </div>
                  <div className="bg-white p-3 sm:p-5 rounded-lg border border-[#222222]/10">
                    <h3 className="font-medium text-base sm:text-lg text-[#222222] mb-2 sm:mb-3 pb-2 border-b border-[#222222]/10">Dates & Status</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Receipt Date:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{formatDate(selectedProperty.startDate)}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Created At:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{formatDate(selectedProperty.createdAt)}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Updated At:</span> <span className="text-xs sm:text-sm font-medium text-[#222222]">{formatDate(selectedProperty.updatedAt)}</span></p>
                      <p className="flex justify-between"><span className="text-xs sm:text-sm text-[#222222]/70">Status:</span> <span className={`text-xs sm:text-sm font-medium ${
                        selectedProperty.status === 'Active' ? 'text-[#1DCD9F]' : 'text-amber-800'
                      }`}>{selectedProperty.status}</span></p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 sm:mt-6 flex justify-end">
                  <button
                    onClick={() => setSelectedProperty(null)}
                    className="px-4 sm:px-6 py-2 bg-[#1DCD9F] text-white rounded-lg hover:bg-[#18b58c] transition-colors shadow-md text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Properties;